import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Primitives";
import { IMAGES } from "@/data";

const VALUES = [
  { num: "01", title: "Accurate", body: "Reporting support you can defend — reconciled, documented, and audit-ready." },
  { num: "02", title: "Strategic", body: "Finance insight that connects the numbers to the decisions in front of you." },
  { num: "03", title: "Practical", body: "Implementation focus. We deliver what works, then make it sustainable." },
];

export default function About() {
  return (
    <>
      <section className="bg-obsidian text-bone">
        <div className="mx-auto max-w-[1400px] px-6 pb-20 pt-40 lg:px-12 lg:pb-28 lg:pt-52">
          <Reveal>
            <Overline dark>About Bridge Advisors</Overline>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[0.95] tracking-tight md:text-7xl">
              Senior financial leadership, <span className="italic text-bone/70">on demand.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-36">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
          <Reveal className="md:col-span-5">
            <Overline>The firm</Overline>
            <h2 className="mt-8 font-serif text-4xl leading-tight tracking-tight text-obsidian md:text-5xl">
              Built to navigate <span className="italic">demanding</span> financial environments.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="space-y-6 text-lg leading-relaxed text-stone md:col-span-7 md:pl-8">
            <p>
              Bridge Advisors LLP helps organizations manage complex financial
              operations with clear reporting, disciplined processes, and
              senior-level advisory support.
            </p>
            <p>
              We work the way US finance teams and their stakeholders expect — US
              GAAP fluency, availability across American time zones, and secure
              remote operations. Our clients get responsive collaboration and
              thoughtful execution, not a rotating bench of juniors.
            </p>
            <p className="text-obsidian">
              The result: leadership teams that trust their numbers and can act on
              them with confidence.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative h-[55vh] overflow-hidden">
        <img src={IMAGES.manifesto} alt="Disciplined architecture" className="h-full w-full object-cover" />
      </section>

      <section data-testid="values-section" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-36">
        <Reveal className="mb-16">
          <Overline>How we work</Overline>
          <h2 className="mt-8 max-w-2xl font-serif text-4xl leading-tight tracking-tight text-obsidian md:text-5xl">
            Three principles behind every engagement.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 border-t border-border md:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.num} delay={i * 0.08}>
              <div className="h-full border-b border-border p-8 md:border-r md:p-10 md:last:border-r-0">
                <span className="font-mono text-sm text-brand">[ {v.num} ]</span>
                <h3 className="mt-10 font-serif text-3xl text-obsidian">{v.title}</h3>
                <p className="mt-4 text-stone">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-24 lg:px-12 lg:pb-36">
        <Reveal className="border border-border bg-sage p-10 text-center md:p-20">
          <h2 className="mx-auto max-w-3xl font-serif text-4xl leading-tight tracking-tight text-obsidian md:text-5xl">
            Ready to bring clarity to your <span className="italic">numbers?</span>
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="group mt-10 inline-flex items-center gap-2 bg-obsidian px-8 py-4 text-sm font-medium text-bone transition-colors duration-300 hover:bg-brand"
          >
            Book a Consultation
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
